import { useState } from 'react'

import { Form, InputLabel, Space, Text, TextButton } from '@procurify/ui'
import { useFormikContext } from 'formik'

import { PriceFormField } from '@webapp/components/PriceField'
import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { Price } from '@webapp/core/components/Price'
import {
  type Currency,
  type CurrencyName,
} from '@webapp/core/entities/external/Currency'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { type Enum } from '@webapp/infrastructure/types/Enum'
import {
  ExpenseMileageConfigurationTaxTypes,
  MileageRatePresetUnits,
  UnitsOfDistance,
  type ExpenseMileageConfiguration,
} from '@webapp/modules/expense/entities'
import { type RequestMileageConfiguration } from '@webapp/modules/expense/entities/RequestMileageConfiguration'
import { TaxFields } from './TaxFields'
import { MileageRatePresetSelect } from '../../MileageRatePresetSelect'
import { type ExpenseLineItemFormSchema } from '../ExpenseLineItemForm'
import messages from '../messages'

const MileageFieldsModes = {
  PRESET: 'preset',
  CUSTOM: 'custom',
} as const
type MileageFieldsMode = Enum<typeof MileageFieldsModes>

interface MileageFieldsProps {
  currencyName: CurrencyName
  selectedCurrency: Currency | null
  onCurrencyChange: (currency: Currency | null) => void
  unitOptions: { label: string; value: string }[]
  computedSubtotal: number
  computedTotal: number
  isSubmitting: boolean
  mileageConfiguration: Nullable<ExpenseMileageConfiguration>
  /**
   * To be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
   */
  mileageLockSetting: RequestMileageConfiguration | null
}

export const MileageFields = ({
  currencyName,
  selectedCurrency,
  onCurrencyChange,
  unitOptions,
  computedSubtotal,
  computedTotal,
  isSubmitting,
  mileageConfiguration,
  mileageLockSetting,
}: MileageFieldsProps) => {
  const intl = useProcurifyIntl()
  const formik = useFormikContext<ExpenseLineItemFormSchema>()

  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )

  const isCustomMileageSet =
    formik.values.mileageRate !== 0 && !formik.values.mileageRatePreset

  const [activeMode, setActiveMode] = useState<MileageFieldsMode>(
    !mileageConfiguration?.forcePresetRates && isCustomMileageSet
      ? MileageFieldsModes.CUSTOM
      : MileageFieldsModes.PRESET
  )
  const showTaxFields =
    !PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS ||
    !mileageConfiguration ||
    mileageConfiguration?.taxType ===
      ExpenseMileageConfigurationTaxTypes.USER_CHOSEN_TAX
  const showUseCustomRateButton = !mileageConfiguration?.forcePresetRates
  /**
   * To be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
   */
  const isMileageLocked = mileageLockSetting?.isLocked ?? false

  return (
    <Space>
      {PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS &&
        activeMode === MileageFieldsModes.PRESET && (
          <Space>
            <>
              <Space align='center' justify='end' direction='horizontal'>
                <InputLabel htmlFor='mileageRate' required>
                  {intl.formatMessage(messages.mileageRateLabel)}
                </InputLabel>
                <Space.Item width={2 / 3}>
                  <MileageRatePresetSelect
                    value={formik.values.mileageRatePreset}
                    onChange={(opt) => {
                      formik.setFieldValue('mileageRatePreset', opt)
                      formik.setFieldValue('mileageRate', opt?.rate)
                      formik.setFieldValue('unitOfDistanceStr', {
                        value:
                          opt?.unit === MileageRatePresetUnits.MILES
                            ? UnitsOfDistance.MILES
                            : UnitsOfDistance.KILOMETERS,
                        label:
                          opt?.unit === MileageRatePresetUnits.MILES
                            ? intl.formatMessage(messages.unitMi)
                            : intl.formatMessage(messages.unitKm),
                      })
                      formik.setFieldValue('currency', opt?.currency ?? null)
                    }}
                    isDisabled={isSubmitting}
                    menuPortalTarget={document.body}
                  />
                </Space.Item>
              </Space>
              {showUseCustomRateButton && (
                <Space direction='horizontal' justify='end'>
                  <TextButton
                    type='button'
                    onClick={() => {
                      formik.setFieldValue('mileageRatePreset', null)
                      setActiveMode(MileageFieldsModes.CUSTOM)
                    }}
                  >
                    {intl.formatMessage(
                      messages.useCustomMileageRateButtonLabel
                    )}
                  </TextButton>
                </Space>
              )}
            </>

            <Space align='center' justify='end' direction='horizontal'>
              <InputLabel htmlFor='distance' required>
                {intl.formatMessage(messages.distanceWithUnitLabel, {
                  unit: formik.values.unitOfDistanceStr.value,
                })}
              </InputLabel>
              <Space.Item width={1 / 3}>
                <Form.NumberField
                  id='distance'
                  name='distance'
                  disabled={isSubmitting}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </Space.Item>
            </Space>
          </Space>
        )}

      {(!PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS ||
        activeMode === MileageFieldsModes.CUSTOM) && (
        <Space>
          <>
            <Space align='center' justify='end' direction='horizontal'>
              <InputLabel htmlFor='mileageRate' required>
                {intl.formatMessage(messages.mileageRateLabel)}
              </InputLabel>
              <Space.Item width={1 / 3}>
                <PriceFormField
                  id='mileageRate'
                  name='mileageRate'
                  disabled={isSubmitting || isMileageLocked}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </Space.Item>
              <Space.Item width={1 / 3}>
                <CurrencySelect
                  inputId='mileageFields-currency-select'
                  value={selectedCurrency}
                  onChange={onCurrencyChange}
                  isDisabled={isSubmitting || isMileageLocked}
                  menuPortalTarget={document.body}
                />
              </Space.Item>
            </Space>
            {PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS && (
              <Space direction='horizontal' justify='end'>
                <TextButton
                  type='button'
                  onClick={() => {
                    formik.setFieldValue('mileageRate', 0)
                    setActiveMode(MileageFieldsModes.PRESET)
                  }}
                >
                  {intl.formatMessage(messages.useMileageRatePresetButtonText)}
                </TextButton>
              </Space>
            )}
          </>

          <Space align='center' justify='end' direction='horizontal'>
            <InputLabel htmlFor='distance' required>
              {intl.formatMessage(messages.distanceLabel)}
            </InputLabel>
            <Space.Item width={1 / 3}>
              <Form.NumberField
                id='distance'
                name='distance'
                disabled={isSubmitting}
                inputProps={{ style: { textAlign: 'right' } }}
              />
            </Space.Item>
            <Space.Item width={1 / 3}>
              <Form.Select
                id='unitOfDistanceStr'
                name='unitOfDistanceStr'
                options={unitOptions}
                disabled={isSubmitting}
              />
            </Space.Item>
          </Space>
        </Space>
      )}

      {showTaxFields && (
        <Space align='center' justify='end' direction='horizontal'>
          <Text bold>{intl.formatMessage(messages.subtotalLabel)}</Text>
          <Price price={computedSubtotal} currency={currencyName} />
        </Space>
      )}

      {showTaxFields && <TaxFields isSubmitting={isSubmitting} />}

      <Space align='center' justify='end' direction='horizontal'>
        <Text bold>{intl.formatMessage(messages.totalLabel)}</Text>
        <Price price={computedTotal} currency={currencyName} />
      </Space>
    </Space>
  )
}
